import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, ChevronDown, ChevronUp, Quote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Char threshold at which we truncate the slogan and show "Visa mer" toggle. */
const SLOGAN_PREVIEW_CHARS = 180;

function SloganBlock({ slogan, lang }) {
  const [expanded, setExpanded] = useState(false);
  if (!slogan) return null;
  const isLong = slogan.length > SLOGAN_PREVIEW_CHARS;
  const shown = expanded || !isLong ? slogan : slogan.slice(0, SLOGAN_PREVIEW_CHARS).trimEnd() + "…";
  const moreLabel = lang === "en" ? "Read more" : "Läs mer";
  const lessLabel = lang === "en" ? "Show less" : "Visa mindre";

  return (
    <div
      style={{
        marginTop: 4,
        paddingTop: 14,
        borderTop: "1px solid rgba(197,160,89,0.15)",
      }}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <Quote
          size={16}
          strokeWidth={1.5}
          style={{ color: "#C5A059", flexShrink: 0, marginTop: 3, opacity: 0.8 }}
        />
        <p
          style={{
            color: "rgba(255,255,255,0.82)",
            fontSize: 14,
            lineHeight: 1.65,
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {shown}
        </p>
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="winner-slogan-toggle"
          style={{
            marginTop: 10,
            marginLeft: 26,
            background: "transparent",
            border: "none",
            color: "#C5A059",
            fontSize: 10.5,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: 0,
          }}
        >
          {expanded ? lessLabel : moreLabel}
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      )}
    </div>
  );
}

/**
 * Public list of contest winners for a given contest_slug.
 * Renders nothing until at least one winner has been picked in admin.
 * Names are already anonymized server-side (first name + last initial).
 */
export default function WinnersSection({ contestSlug, lang = "sv" }) {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/contest/${contestSlug}/winners`)
      .then((r) => {
        if (!cancelled) setWinners(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setWinners([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contestSlug]);

  if (loading || winners.length === 0) return null;

  const t =
    lang === "en"
      ? { heading: "Our winners", subheading: "Congratulations to the weekly winners so far", weekLabel: "Week" }
      : { heading: "Våra vinnare", subheading: "Grattis till veckans vinnare hittills", weekLabel: "Vecka" };

  return (
    <section
      data-testid="winners-section"
      className="my-12"
      style={{
        background: "linear-gradient(180deg,rgba(197,160,89,0.08) 0%,rgba(197,160,89,0.02) 100%)",
        border: "1px solid rgba(197,160,89,0.35)",
        padding: "48px 32px",
        borderRadius: "2px",
      }}
    >
      <div className="text-center mb-10">
        <div
          className="inline-flex items-center gap-2 mb-4"
          style={{
            color: "#C5A059",
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontFamily: "'Outfit',sans-serif",
            fontWeight: 500,
          }}
        >
          <Trophy size={14} strokeWidth={1.5} />
          <span>{t.subheading}</span>
        </div>
        <h2
          className="font-['Cormorant_Garamond']"
          style={{ color: "#fff", fontSize: 36, fontWeight: 300, margin: 0, lineHeight: 1.15, letterSpacing: "-0.01em" }}
        >
          {t.heading}
        </h2>
      </div>

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          maxWidth: 1100,
          margin: "0 auto",
          alignItems: "start",
        }}
      >
        {winners.map((w) => (
          <div
            key={`${w.week}-${w.display_name}`}
            data-testid={`winner-card-week-${w.week}`}
            style={{
              background: "rgba(10,10,10,0.55)",
              border: "1px solid rgba(197,160,89,0.25)",
              padding: "24px 22px",
              borderRadius: "2px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div
              style={{
                color: "#C5A059",
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                fontFamily: "'Outfit',sans-serif",
                fontWeight: 600,
              }}
            >
              {t.weekLabel} {w.week}
            </div>
            <div
              className="font-['Cormorant_Garamond']"
              style={{ color: "#fff", fontSize: 24, fontWeight: 400, lineHeight: 1.2 }}
            >
              {w.display_name}
            </div>
            {w.prize && (
              <div
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 14,
                  lineHeight: 1.55,
                  fontFamily: "'Outfit',sans-serif",
                }}
              >
                {w.prize}
              </div>
            )}
            <SloganBlock slogan={w.slogan} lang={lang} />
          </div>
        ))}
      </div>
    </section>
  );
}
